import * as Helpers from "./helpers/Calculations";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import useSavingProposal from "@portal/hooks/robot/useSavingProposal";
import Typography from "@portal/components/atoms/v2/typography";
import { MainBox } from "../components/mainBox";
import { ExpectedValueData } from "../components/highcharts/ExpectedValueData";
import { AnnualResultOverview, MoneyResultOverview } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import Link from "next/link";
import { clearHasError, setLoading } from "@portal/redux/reducers/ui/createSavingsAgreement";

export const ExpectedValuePage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const dispatch = useAppDispatch();
  const request = useAppSelector(state => state.persisted.savingProposal);
  const savingProposal = useSavingProposal({ ...request, centerId });
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const isButtonDisabled = savingProposal.error !== undefined && savingProposal.error !== null;
  const riskLevel = request.savingAgreementData.riskLevel;

  const calculationsResponse = useAppSelector(state => state.persisted.savingCalculations.response);
  const selectedSavingCalculations = Helpers.GetSavingCalculations(riskLevel, calculationsResponse.data)?.expectedReturn;
  const proposalResponse = savingProposal.data;
  const dates = Helpers.GetDatesForChart(proposalResponse?.calculations);

  const refundProvisionAmounts = Helpers.GetRefundProvisionAmounts(proposalResponse?.calculations);
  const managmentFees = Helpers.GetManagementFees(proposalResponse?.calculations);
  const managmentFeeAfterTenYears = Helpers.GetManagementFeeForDecade(managmentFees[2], refundProvisionAmounts[2]);
  const managmentFeeAfterOneYear = Helpers.GetManagementFeeForDecade(managmentFees[0], refundProvisionAmounts[0]);
  const depotFees = Helpers.GetDepotFees(proposalResponse?.calculations);
  const depotFeeAfterOneYear = depotFees[0] ?? 0;
  const depotFeeAfterTenYears = depotFees[2] ?? 0;

  const portfolioValues = Helpers.GetPortfolioValues(selectedSavingCalculations);
  const portfolioValueAfterOneYear = portfolioValues[0] ?? 0;
  const portfolioValueAfterTenYears = portfolioValues[2] ?? 0;
  const totalCostAfterOneYear = managmentFeeAfterOneYear + depotFeeAfterOneYear;
  const totalCostAfterTenYears = managmentFeeAfterTenYears + depotFeeAfterTenYears;
  const totalValueAfterCostOneYear = portfolioValueAfterOneYear - totalCostAfterOneYear;
  const totalValueAfterCost = portfolioValueAfterTenYears - totalCostAfterTenYears;

  const returnsWithoutFee = proposalResponse?.returnsWithoutFeePercentage;
  const returnsWithFee = Helpers.GetReturnsWithFee(proposalResponse?.returnsWithoutFeePercentage, proposalResponse?.summedFee);
  const showReturns = returnsWithFee !== null && returnsWithoutFee !== null && returnsWithoutFee !== undefined;

  return (
    <>
      <MainBox>
        {showReturns && (
          <Typography variant="H6">
            {`${texts.PAGE_15_EXPECTED_PROFIT} 
      ${returnsWithoutFee.toFixed(2)}${texts.PAGE_15_PERCENTAGE_SIGN} ${texts.PAGE_15_BEFORE_COST} 
      ${returnsWithFee.toFixed(2)}${texts.PAGE_15_PERCENTAGE_SIGN} ${texts.PAGE_15_AFTER_COST}`}
          </Typography>
        )}
        <TextLine />
        <div className="flex flex-col my-3 border rounded-md w-full p-1 bg-white">
          <HighchartsReact
            highcharts={Highcharts}
            options={ExpectedValueData(managmentFees, depotFees, portfolioValues, isDefaultLanguage, dates)}
          />
        </div>
        <div className="flex flex-col mt-2 space-y-5 md:space-y-0 md:flex-row md:space-x-5">
          <div className="w-full space-y-5">
            <MoneyResultOverview
              title={texts.PAGE_15_ANNUAL}
              isDefaultLanguage={isDefaultLanguage}
              managementFee={managmentFeeAfterOneYear}
              depotFee={depotFeeAfterOneYear}
              summedFee={totalCostAfterOneYear}
              totalValueAfterCost={totalValueAfterCostOneYear}
              portfolioValue={portfolioValueAfterOneYear}
            />
            <AnnualResultOverview
              isDefaultLanguage={isDefaultLanguage}
              managmentFee={Helpers.GetManagementFee(proposalResponse?.summedFee, proposalResponse?.depotFee)}
              depotFee={proposalResponse?.depotFee}
              summedFee={proposalResponse?.summedFee}
            />
          </div>
          <div className="w-full">
            <MoneyResultOverview
              title={texts.PAGE_15_DECADE}
              isDefaultLanguage={isDefaultLanguage}
              managementFee={managmentFeeAfterTenYears}
              depotFee={depotFeeAfterTenYears}
              summedFee={totalCostAfterTenYears}
              totalValueAfterCost={totalValueAfterCost}
              portfolioValue={portfolioValueAfterTenYears}
            />
          </div>
        </div>
        <div className="my-3">
          <Typography variant="Body">
            {texts.PAGE_15_FINANCE_PORTAL_PRE_TEXT}
            <Link href="https://www.forbrukerradet.no/fond" target="_blank" className="text-blueHighlighted">
              {texts.PAGE_15_FINANCE_PORTAL_LINK_TEXT}
            </Link>
          </Typography>
        </div>
        {isButtonDisabled && <InfoBoxComponent texts={[texts.ERROR_FETCH]} warning />}
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isButtonDisabled}
        onNextClick={() => {
          dispatch(setLoading(false));
          dispatch(clearHasError());
        }}
      />
    </>
  );
};
